import type { Analysis } from '../../types/models';
// import { ScoreCard } from './ScoreCard'; // Removed unused import
import { TechnicalAnalysis } from './TechnicalAnalysis';
import { RiskFactors } from './RiskFactors';
import { GrowthPotential } from './GrowthPotential';
import { InterviewPlan } from './InterviewPlan';
import { AnalysisSummary } from './AnalysisSummary';
import { AnalysisNotes } from './AnalysisNotes';
import { ExportButton } from './ExportButton';
import { VerdictCard } from './VerdictCard';
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useEffect, useState } from 'react';

interface InterviewSection {
  title: string;
  duration: string;
  questions: string[];
  redFlags: string[];
  greenFlags: string[];
}

type InterviewerPerspective = 'hr' | 'manager' | 'senior-manager' | 'c-level' | 'technical-lead';
// Import icons for accordion items
import { ClipboardCheck, Wrench, ShieldAlert, TrendingUp, ListChecks, StickyNote, UserPlus } from 'lucide-react';

interface AnalysisResultsProps {
  analysis: Analysis;
  projectId?: string;
  onAnalysisComplete?: (analysisResult: any) => void;
}

export function AnalysisResults({ analysis, projectId, onAnalysisComplete }: AnalysisResultsProps) {
  const [selectedPerspective, setSelectedPerspective] = useState<InterviewerPerspective>('manager');
  const [transformedSections, setTransformedSections] = useState<InterviewSection[]>([]);

  // Helper to get score color classes (adjusting for theme)
  function getScoreColorClasses(score: number): string {
    if (score >= 8) return "text-green-600"; 
    if (score >= 4) return "text-amber-600";
    return "text-red-600"; 
  }
  const scoreColor = getScoreColorClasses(analysis.fit_score);

  // Auto-trigger analysis completion for project context
  useEffect(() => {
    if (projectId && onAnalysisComplete && analysis) {
      // Automatically add to project when analysis is complete
      const candidateData = {
        candidateName: 'Analyzed Candidate', // Analysis doesn't store candidate name directly
        email: '', // Not available in analysis
        overallScore: analysis.fit_score,
        technicalScore: analysis.technical_skills?.length > 0 ? 8.5 : 0, // Estimated from skills
        experienceScore: 7.5, // Estimated - not directly available
        culturalFitScore: 7.0, // Estimated - not directly available
        verdict: analysis.verdict,
        strengths: analysis.reasoning?.slice(0, 3) || [], // Use reasoning as strengths
        weaknesses: analysis.risk_factors?.map(rf => rf.description).slice(0, 2) || [],
        matchedSkills: analysis.technical_skills?.map(ts => ts.skill) || [],
        missingSkills: [], // Not directly available in analysis
        interviewQuestions: [], // Interview plan is string, not array
        resumeUrl: '',
        analysisDate: new Date(analysis.created_at)
      };
      
      // Call after a short delay to ensure UI is rendered
      setTimeout(() => {
        onAnalysisComplete(candidateData);
      }, 1000);
    }
  }, [analysis, projectId, onAnalysisComplete]);

  const handleAddToProject = () => {
    if (onAnalysisComplete && analysis) {
      const candidateData = {
        candidateName: 'Analyzed Candidate',
        email: '',
        overallScore: analysis.fit_score,
        technicalScore: analysis.technical_skills?.length > 0 ? 8.5 : 0,
        experienceScore: 7.5,
        culturalFitScore: 7.0,
        verdict: analysis.verdict,
        strengths: analysis.reasoning?.slice(0, 3) || [],
        weaknesses: analysis.risk_factors?.map(rf => rf.description).slice(0, 2) || [],
        matchedSkills: analysis.technical_skills?.map(ts => ts.skill) || [],
        missingSkills: [],
        interviewQuestions: [],
        resumeUrl: '',
        analysisDate: new Date(analysis.created_at)
      };
      
      onAnalysisComplete(candidateData);
    }
  };

  return (
    <div className="space-y-8">
      {/* Project Context Notice */}
      {projectId && (
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border-blue-200/60 rounded-2xl shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center shadow-lg">
                  <ClipboardCheck className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-blue-900">Project Analysis Complete</p>
                  <p className="text-sm text-blue-700 mt-1">
                    This candidate has been analyzed for your project and automatically added to the pipeline.
                  </p>
                </div>
              </div>
              {onAnalysisComplete && (
                <Button 
                  onClick={handleAddToProject}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg transition-all duration-300"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add to Project
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Section: Score and Verdict - Use Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
         {/* Score Card */}
        <Card className="md:col-span-1 flex flex-col items-center justify-center text-center p-8 bg-gradient-to-br from-slate-50 to-slate-100/50  rounded-2xl shadow-sm border border-slate-200/60">
           {/* Apply color class to the score text */}
          <span className={cn("text-7xl font-bold", scoreColor)}>{analysis.fit_score}</span> 
          <span className="mt-2 text-sm font-semibold text-slate-600 tracking-wide">FIT SCORE</span>
        </Card>
         {/* Verdict Card */}
        <Card className="md:col-span-2 rounded-2xl shadow-sm border border-slate-200/60 bg-white/95 backdrop-blur-sm">
           {/* VerdictCard component likely needs its own CardHeader/CardContent internally */}
           {/* Assuming VerdictCard handles its own internal structure */}
          <CardHeader className="pb-4">
             <CardTitle className="text-xl font-bold text-slate-900 tracking-tight">Verdict & Reasoning</CardTitle>
          </CardHeader>
          <CardContent>
             <VerdictCard verdict={analysis.verdict} reasoning={analysis.reasoning} />
          </CardContent>
        </Card>
      </div>

      {/* Accordion for Details Sections - Add icons and subtle background hints */}
      <Accordion type="multiple" defaultValue={['interview']} className="w-full space-y-6">
        
        <AccordionItem value="summary" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
           {/* Added icon and subtle hover/focus background */}
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
               <div className="h-8 w-8 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-200">
                 <ClipboardCheck className="h-4 w-4 text-blue-600" />
               </div>
               <span>Candidate vs. Position Summary</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <AnalysisSummary analysis={analysis} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="technical" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
           <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
             <div className="flex items-center space-x-4">
                <div className="h-8 w-8 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors duration-200">
                  <Wrench className="h-4 w-4 text-purple-600" />
                </div>
                <span>Technical Skills Analysis</span>
             </div>
           </AccordionTrigger>
           <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
             <TechnicalAnalysis analysis={analysis} />
           </AccordionContent>
         </AccordionItem>

        {/* Risk Factors and Growth Potential - Now vertically stacked */}
        <AccordionItem value="risk" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-red-100 rounded-xl flex items-center justify-center group-hover:bg-red-200 transition-colors duration-200">
                <ShieldAlert className="h-4 w-4 text-red-600" />
              </div>
              <span>Risk Factors</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <RiskFactors factors={analysis.risk_factors} />
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="growth" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
          <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors duration-200">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <span>Growth Potential</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <GrowthPotential potential={analysis.growth_potential} />
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="interview" className="border border-slate-200/60 rounded-2xl overflow-hidden bg-white/95 backdrop-blur-sm shadow-sm">
           <AccordionTrigger className="px-6 sm:px-8 py-6 hover:bg-slate-50/50 focus-visible:bg-slate-50/50 text-base font-semibold group transition-all duration-200">
             <div className="flex items-center space-x-4">
                <div className="h-8 w-8 bg-indigo-100 rounded-xl flex items-center justify-center group-hover:bg-indigo-200 transition-colors duration-200">
                  <ListChecks className="h-4 w-4 text-indigo-600" />
                </div>
                <span>Suggested Interview Plan</span>
             </div>
           </AccordionTrigger>
                     <AccordionContent className="px-6 sm:px-8 py-6 border-t border-slate-100 bg-white">
            <InterviewPlan 
              plan={analysis.interview_plan}
              perspectives={analysis.interview_perspectives}
              initialPerspective={analysis.selected_perspective || 'manager'}
              analysisId={analysis.id}
              onPerspectiveChange={setSelectedPerspective}
              onTransformedSectionsChange={setTransformedSections}
            />
          </AccordionContent>
         </AccordionItem>
        
        <AccordionItem value="notes" className="border rounded-lg overflow-hidden bg-background">
            <AccordionTrigger className="px-4 sm:px-6 py-3 hover:bg-accent/50 focus-visible:bg-accent/50 text-base font-semibold group">
              <div className="flex items-center space-x-3">
                 <StickyNote className="h-5 w-5 text-gray-500" /> 
                 <span>Notes</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 sm:px-6 py-4 border-t bg-background">
              <AnalysisNotes analysis={analysis} />
            </AccordionContent>
          </AccordionItem>

      </Accordion>

      {/* Bottom Section: Export Button */}
      <div className="flex justify-between items-center pt-4">
        {onAnalysisComplete && !projectId && (
          <Button 
            onClick={handleAddToProject}
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add to Project
          </Button>
        )}
        <div className="ml-auto">
          <ExportButton 
            analysis={analysis} 
            selectedPerspective={selectedPerspective}
            transformedSections={transformedSections}
          />
        </div>
      </div>
    </div>
  );
}